export type MonthBucket = 'past' | 'currentOrNext' | 'future';
export type ChartStage = 'Booked' | 'Pipe' | 'Forecast';

function yearMonthKey(date: Date): number {
  return date.getFullYear() * 12 + date.getMonth();
}

/** Past = before current calendar month; currentOrNext = this month + next; future = after next month. */
export function getMonthBucket(monthDate: Date, today: Date = new Date()): MonthBucket {
  const rowYm = yearMonthKey(monthDate);
  const currentYm = yearMonthKey(today);
  const nextYm = currentYm + 1;

  if (rowYm < currentYm) return 'past';
  if (rowYm === currentYm || rowYm === nextYm) return 'currentOrNext';
  return 'future';
}

/** Pipe = 70% Verbal Confirmation or 90% IO Pending in Status. */
export function isPipeStatus(status?: string): boolean {
  const s = status?.trim().toLowerCase() ?? '';
  if (!s) return false;
  return (
    (s.includes('70%') && s.includes('verbal')) ||
    (s.includes('90%') && s.includes('io pending'))
  );
}

export function isBookedStatus(status?: string): boolean {
  if (isPipeStatus(status)) return false;
  const t = status?.trim().toLowerCase() ?? '';
  if (!t) return false;
  return t === 'booked' || t.includes('booked') || /^100%/.test(t);
}

export function isForecastStatus(status?: string): boolean {
  if (isPipeStatus(status) || isBookedStatus(status)) return false;
  const t = status?.trim().toLowerCase() ?? '';
  if (!t) return false;
  return t === 'forecast' || t.includes('forecast') || t === 'fc' || t === 'fcst';
}

/** Classify a row's operational stage from Status (pipe rules) with Simplified_Stages fallback. */
export function classifyOperationalStage(status?: string, simplified?: string): ChartStage | 'Other' {
  if (isPipeStatus(status)) return 'Pipe';
  if (isBookedStatus(status)) return 'Booked';
  if (isForecastStatus(status)) return 'Forecast';

  const sim = simplified?.trim().toLowerCase() ?? '';
  if (sim === 'pipe') return 'Pipe';
  if (sim === 'booked' || sim === 'book') return 'Booked';
  if (sim === 'forecast' || sim === 'fc' || sim === 'fcst') return 'Forecast';

  return 'Other';
}

/**
 * Which chart segment a row contributes to, based on its month vs today:
 * - Past months: Booked only
 * - Current + next month: Booked and Pipe (stacked)
 * - After next month: Booked and Forecast (stacked)
 *
 * Booked stays visible in later months: it was dropped there, so from two
 * months out the chart showed forecast alone while the cards above still
 * counted what was already booked.
 */
export function chartStageForMonthRow(
  monthDate: Date,
  status?: string,
  simplified?: string,
  today: Date = new Date()
): ChartStage | null {
  const bucket = getMonthBucket(monthDate, today);
  const stage = classifyOperationalStage(status, simplified);

  if (bucket === 'past') {
    if (stage === 'Pipe' || stage === 'Forecast') return null;
    return 'Booked';
  }

  if (bucket === 'currentOrNext') {
    if (stage === 'Booked' || stage === 'Pipe') return stage;
    return null;
  }

  return stage === 'Forecast' || stage === 'Booked' ? stage : null;
}
